import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { Loader } from 'lucide-react';
import { FileType } from '../../../types';
import CitationTooltip from './CitationTooltip';
import { cn } from '../../../lib/utils';

export type AssistantFileType = FileType & {
  fileId?: string;
  fileName?: string;
};

interface AnswerDisplayProps {
  aiAnswer: string | null;
  isGeneratingAnswer: boolean;
  lastSearchQuery: string | null;
  results: {
    data: AssistantFileType[];
    error: string | null;
  };
  featuredThumbnail: {
    fileId: string;
    fileName: string;
    timestamp: string;
    thumbnailUrl: string | null;
    isLoading: boolean;
  } | null;
  activeSource: { fileId: string; timestamp: string } | null;
  handleSourceClick: (fileId: string, timestamp: string) => void;
  citationThumbnails: Array<{
    fileId: string;
    timestamp: string;
    thumbnailUrl: string | null;
    isLoading: boolean;
  }>;
  fetchCitationThumbnail: (fileId: string, timestamp: string) => void;
  formatTimestamp: (seconds: string) => string;
  hideReferences: boolean;
  onClearHistory?: () => void;
  showVideoPlayer?: boolean;
  videoPlayerFile?: FileType | null;
}

const AnswerDisplay: React.FC<AnswerDisplayProps> = ({
  aiAnswer,
  isGeneratingAnswer,
  results,
  activeSource,
  handleSourceClick,
  citationThumbnails,
  fetchCitationThumbnail,
  formatTimestamp,
  hideReferences,
}) => {
  const { t } = useTranslation();

  // Always call all hooks first to maintain hook order
  const citations = React.useMemo(() => {
    if (!aiAnswer) return [];
    const matches = aiAnswer.matchAll(/\[→\]\(([^@]+)@([^)]+)\)/g);
    return Array.from(matches).map(([, fileId, timestamp]) => ({
      fileId,
      timestamp,
    }));
  }, [aiAnswer]);

  const getFileForCitation = React.useCallback(
    (fileId: string) =>
      results.data.find(
        (file) => file._id === fileId || file.fileId === fileId,
      ),
    [results.data],
  );

  const answerWithLinks = React.useMemo(() => {
    return aiAnswer?.replace(
      /\[→\]\(([^@]+)@([^)]+)\)/g,
      (_, fileId, timestamp) => {
        return `[→](citation-link:${fileId}@${timestamp})  `;
      },
    );
  }, [aiAnswer]);

  const replacedAnswer = React.useMemo(() => {
    return hideReferences
      ? answerWithLinks?.replace(/### References[\s\S]*$/, '')
      : answerWithLinks;
  }, [hideReferences, answerWithLinks]);

  const referencesSection = React.useMemo(() => {
    return aiAnswer?.match(/### References[\s\S]*$/)?.[0];
  }, [aiAnswer]);

  const references = React.useMemo(() => {
    if (!referencesSection) return [];
    const refMatches = referencesSection.matchAll(
      /\[(\d+)\]\(([^)]+)\):\s*([^\n]+)/g,
    );
    return Array.from(refMatches).map(([, number, fileId, title]) => {
      const file = getFileForCitation(fileId);
      return {
        number,
        fileId,
        title,
        fileName: file?.name || file?.fileName || title,
      };
    });
  }, [referencesSection, getFileForCitation]);

  const isCurrentSourceActive = React.useCallback(
    (fileId: string, timestamp: string) =>
      activeSource?.fileId === fileId && activeSource?.timestamp === timestamp,
    [activeSource],
  );

  React.useEffect(() => {
    citations.forEach(({ fileId, timestamp }) => {
      fetchCitationThumbnail(fileId, timestamp);
    });
  }, [citations, fetchCitationThumbnail]);

  // Debug logging
  React.useEffect(() => {
    if (aiAnswer) {
      console.log('AnswerDisplay Debug:', {
        aiAnswer: `${aiAnswer.substring(0, 300)}...`,
        answerWithLinks: `${answerWithLinks?.substring(0, 300)}...`,
        replacedAnswer: `${replacedAnswer?.substring(0, 300)}...`,
        hideReferences,
      });
    }
  }, [aiAnswer, answerWithLinks, replacedAnswer, hideReferences]);

  // Handle conditional rendering after all hooks
  if (isGeneratingAnswer && !aiAnswer) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader className="w-6 h-6 animate-spin text-muted-foreground" />
        <span className="ml-3 text-sm text-muted-foreground">
          {t('assistant.answer.generating')}
        </span>
      </div>
    );
  }

  if (!aiAnswer && !isGeneratingAnswer) {
    return null;
  }

  return (
    <div className="flex gap-4 p-2">
      {/* AI Response */}
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            'prose prose-sm dark:prose-invert max-w-none',
            'text-sm text-foreground leading-relaxed',
            '[&>p]:mb-3 [&>ul]:mb-3 [&>ol]:mb-3',
            '[&_li]:text-sm [&_li]:leading-relaxed',
          )}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ children, href, ...props }) => {
                const match = href?.match(/^citation-link:(.+)@(.+)$/);
                if (match) {
                  const [, fileId, timestamp] = match;
                  const file = getFileForCitation(fileId);
                  const citationThumbnail = citationThumbnails.find(
                    (thumb) =>
                      thumb.fileId === fileId && thumb.timestamp === timestamp,
                  );
                  return (
                    <CitationTooltip
                      fileId={fileId}
                      timestamp={timestamp}
                      fileName={file?.name || file?.fileName || 'Recording'}
                      file={file}
                      thumbnailUrl={citationThumbnail?.thumbnailUrl || null}
                      isLoading={citationThumbnail?.isLoading || false}
                      formatTimestamp={formatTimestamp}
                      onClick={() => handleSourceClick(fileId, timestamp)}
                      isActive={isCurrentSourceActive(fileId, timestamp)}
                    >
                      {children}
                    </CitationTooltip>
                  );
                }

                // Handle empty href case - these are timestamp citations without proper fileId
                if (!href || href === '') {
                  // Check if children contains just the arrow symbol (→)
                  const childrenStr = String(children);
                  if (childrenStr === '→' || childrenStr.includes('→')) {
                    // This is a malformed citation - just show the arrow without making it a link
                    // Use the first file from results if we have one
                    const firstFile = results.data?.[0];
                    if (firstFile) {
                      // Try to extract any number from the context that could be a timestamp
                      // For now, just link to the beginning of the first file
                      return (
                        <CitationTooltip
                          fileId={firstFile._id || firstFile.fileId || ''}
                          timestamp="0"
                          fileName={
                            firstFile.name || firstFile.fileName || 'Recording'
                          }
                          file={firstFile}
                          thumbnailUrl={null}
                          isLoading={false}
                          formatTimestamp={formatTimestamp}
                          onClick={() =>
                            handleSourceClick(
                              firstFile._id || firstFile.fileId || '',
                              '0',
                            )
                          }
                          isActive={isCurrentSourceActive(
                            firstFile._id || firstFile.fileId || '',
                            '0',
                          )}
                        >
                          {children}
                        </CitationTooltip>
                      );
                    }

                    // If no file available, just return the arrow without link styling
                    return <span>{children}</span>;
                  }

                  // Try to extract timestamp from children if it's in format like "5:29"
                  const timeMatch = childrenStr.match(/^(\d+):(\d+)$/);
                  if (timeMatch) {
                    const [, minutes, seconds] = timeMatch;
                    const totalSeconds =
                      parseInt(minutes, 10) * 60 + parseInt(seconds, 10);

                    // Use the first file from results if available
                    const firstFile = results.data?.[0];
                    if (firstFile) {
                      return (
                        <CitationTooltip
                          fileId={firstFile._id || firstFile.fileId || ''}
                          timestamp={String(totalSeconds)}
                          fileName={
                            firstFile.name || firstFile.fileName || 'Recording'
                          }
                          file={firstFile}
                          thumbnailUrl={null}
                          isLoading={false}
                          formatTimestamp={formatTimestamp}
                          onClick={() =>
                            handleSourceClick(
                              firstFile._id || firstFile.fileId || '',
                              String(totalSeconds),
                            )
                          }
                          isActive={isCurrentSourceActive(
                            firstFile._id || firstFile.fileId || '',
                            String(totalSeconds),
                          )}
                        >
                          {children}
                        </CitationTooltip>
                      );
                    }

                    // Fallback if no file is available - still show as clickable blue link
                    return (
                      <span
                        className="text-primary cursor-pointer hover:underline"
                        title="Citation link unavailable"
                      >
                        {children}
                      </span>
                    );
                  }
                }

                return (
                  <a
                    href={href}
                    {...props}
                    className="text-primary hover:underline"
                  >
                    {children}
                  </a>
                );
              },
            }}
          >
            {replacedAnswer || ''}
          </ReactMarkdown>
        </div>

        {!hideReferences && references.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <h3 className="text-sm font-semibold mb-3 text-foreground">
              References
            </h3>
            <div className="space-y-2">
              {references.map((ref) => (
                <button
                  type="button"
                  key={ref.number}
                  onClick={() => handleSourceClick(ref.fileId, '0')}
                  className="block w-full text-left p-2 rounded-lg hover:bg-black/[0.02]"
                >
                  <span className="text-xs text-muted-foreground">
                    [{ref.number}]
                  </span>{' '}
                  <span className="text-sm text-foreground hover:underline">
                    {ref.fileName}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {isGeneratingAnswer && aiAnswer && (
          <div className="flex items-center justify-center p-2">
            <Loader className="w-4 h-4 animate-spin text-muted-foreground" />
            <span className="ml-2 text-xs text-muted-foreground">
              {t('assistant.refiningAnswer')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnswerDisplay;
